import React, { FC, ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isProduction } from "../services/config";
import {
  HsCard,
  HsDialog,
  HsDropdownButton,
  HsHighlights,
  HsMenu,
  HsPagination,
  HsPopover,
  HsRecordAction,
  HsSkeletonRow,
  HsTabs,
  HsViewTabs,
  OuCheckbox,
  OuIconButton,
  OuPrimaryButton,
  OuSearch,
  OuSecondaryButton,
  OuSelect,
  OuSwitch,
  OuTable,
  OuTooltip,
  OuPill,
  OuNotice,
  OuBone,
  HsAdvancedFiltersButton,
  HsBadge,
  HsCountTabs,
  HsDashboardTitle,
  HsDataWell,
  HsFilterPopoverHead,
  HsQuickFilter,
  HsReportCard,
} from "../components/HubSpotUi";
import { HubSpotBoxedTabs } from "../components/Layout/HubSpotAppShell";
import Input from "../components/DataInput/Input";
import Select from "../components/DataInput/Select";
import {
  HsCallIcon,
  HsDragIcon,
  HsEmailIcon,
  HsExportIcon,
  HsExternalIcon,
  HsFilterIcon,
  HsListIcon,
  HsRightIcon,
  HsSettingsIcon,
  HsCalendarIcon,
  HsLeftIcon,
} from "../components/HubSpotUi/icons";

/**
 * Development and staging only: every part of the HubSpot kit in each of its
 * states, for eyeballing and for app/scripts/hubspot-visual, which diffs each
 * one against the live HubSpot capture it was built from. `?only=<id>` shows
 * a single sample on a white page (the harness's view); without it, the
 * whole gallery. Open with ?crmTheme=hubspot so the page carries HubSpot's look.
 *
 * In each sample [data-kit-root] marks the box that is compared and
 * [data-kit-target] the part that takes :hover / :focus-visible / :active.
 */

type State = "default" | "hover" | "focus" | "active" | "open";
/* The state hook HubSpot's own rules use too (data-vr-state there). */
const st = (s: State) => (s === "default" || s === "open" ? {} : { "data-hs-state": s });

interface Sample {
  id: string;
  label: string;
  states: State[];
  /** Renders a floating part (menu, dialog, tooltip): one per page. */
  floating?: boolean;
  render: (s: State) => ReactNode;
}

const Avatar: FC = () => <span style={{ display: "inline-block", width: 24, height: 24, borderRadius: "50%", background: "#ebebeb", flex: "0 0 auto" }} />;

interface Row {
  id: string;
  name: string;
  email: string;
}

const ROWS: Row[] = [
  { id: "1", name: "Ada Lovelace", email: "ada@example.com" },
  { id: "2", name: "Grace Hopper", email: "grace@example.com" },
];

const COLUMNS = ["Name", "Email", "Email status", "Email verification date", "Lead status", "Last activity", "Contact owner", "Phone number", "Create date"];

const TableSample: FC = () => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  return (
    <OuTable<Row>
      rows={ROWS}
      getRowId={(r) => r.id}
      selection={{ selected, onChange: setSelected }}
      columns={COLUMNS.map((header, i) => ({
        key: header,
        header,
        width: 250,
        render: (r: Row) =>
          i === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-flex", width: 16, justifyContent: "center" }}>
                <HsRightIcon width={14} height={14} />
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <Avatar />
                <a href="#row" className="hs-cell">
                  {r.name}
                </a>
              </span>
            </div>
          ) : i === 1 ? (
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <a href="#row">{r.email}</a>
              <HsExternalIcon width={14} height={14} style={{ margin: "0 4px", color: "var(--hs-link)" }} />
            </span>
          ) : (
            "--"
          ),
      }))}
    />
  );
};

const SelectSample: FC<{ s: State }> = () => {
  const [value, setValue] = useState("");
  return (
    <div data-kit-root style={{ width: 279, display: "flex" }}>
      <OuSelect value={value} onChange={setValue} placeholder="Select a record" options={[{ value: "a", label: "Ada Lovelace" }]} />
    </div>
  );
};

const SAMPLES: Sample[] = [
  {
    id: "primary-button",
    label: "Primary button (dropdown)",
    states: ["default", "hover", "active", "focus"],
    render: (s) => (
      <HsDropdownButton use="primary" data-kit-root data-kit-target {...st(s)}>
        Add contacts
      </HsDropdownButton>
    ),
  },
  {
    id: "secondary-button",
    label: "Secondary button with icon",
    states: ["default", "hover", "active", "focus"],
    render: (s) => (
      <OuSecondaryButton size="small" icon={<HsExportIcon />} data-kit-root data-kit-target {...st(s)}>
        Export
      </OuSecondaryButton>
    ),
  },
  {
    id: "primary-button-large",
    label: "Primary button, large",
    states: ["default", "hover", "active", "focus"],
    render: (s) => (
      <OuPrimaryButton endIcon={<HsExternalIcon />} data-kit-root data-kit-target {...st(s)}>
        Get started
      </OuPrimaryButton>
    ),
  },
  {
    id: "secondary-button-large",
    label: "Secondary button, large",
    states: ["default", "hover", "focus"],
    render: (s) => (
      <OuSecondaryButton data-kit-root data-kit-target {...st(s)}>
        Cancel
      </OuSecondaryButton>
    ),
  },
  {
    id: "disabled-button-large",
    label: "Primary button, disabled",
    states: ["default"],
    render: () => (
      <OuPrimaryButton disabled data-kit-root data-kit-target>
        Merge
      </OuPrimaryButton>
    ),
  },
  {
    id: "filter-pill",
    label: "Toolbar filter pill (pressed)",
    states: ["default", "hover", "focus"],
    render: (s) => (
      <OuIconButton label="Filter" pressed className="hs-btn--tight" data-kit-root data-kit-target {...st(s)}>
        <HsFilterIcon />
      </OuIconButton>
    ),
  },
  {
    id: "icon-button",
    label: "Icon button",
    states: ["default", "hover", "focus"],
    render: (s) => (
      <OuIconButton aria-label="Open task list" data-kit-root data-kit-target {...st(s)}>
        <HsListIcon />
      </OuIconButton>
    ),
  },
  {
    id: "record-action",
    label: "Record action",
    states: ["default", "hover"],
    render: () => (
      <div data-kit-root style={{ display: "inline-flex" }}>
        <HsRecordAction icon={<HsEmailIcon />} label="Email" />
      </div>
    ),
  },
  {
    id: "checkbox",
    label: "Checkbox",
    states: ["default"],
    render: () => <CheckboxSample />,
  },
  {
    id: "search-input",
    label: "Search input",
    states: ["default", "focus"],
    render: () => (
      <div data-kit-root style={{ width: 160 }}>
        <SearchSample />
      </div>
    ),
  },
  {
    id: "select",
    label: "Select",
    states: ["default", "hover", "focus"],
    render: (s) => <SelectSample s={s} />,
  },
  {
    id: "select-menu",
    label: "Select menu (open)",
    states: ["open"],
    floating: true,
    render: () => (
      <HsMenu
        flush
        open
        anchorReference="anchorPosition"
        anchorPosition={{ top: 120, left: 120 }}
        transitionDuration={0}
        PaperProps={{ style: { width: 252, maxHeight: 222 } }}
        MenuListProps={{ role: "listbox", ...({ "data-kit-root": "" } as object) }}
      >
        {["ada@example.com", "grace@example.com", "katherine@example.com", "dorothy@example.com", "mary@example.com"].map((v, i) => (
          <li key={v} role="none">
            <button type="button" role="option" aria-selected={false} className={`hs-menu__item hs-menu__item--option${i === 0 ? " is-selected" : ""}`}>
              {v}
            </button>
          </li>
        ))}
      </HsMenu>
    ),
  },
  {
    id: "dropdown-menu",
    label: "Dropdown menu (open)",
    states: ["open"],
    floating: true,
    render: () => (
      <HsMenu
        open
        anchorReference="anchorPosition"
        anchorPosition={{ top: 120, left: 120 }}
        transitionDuration={0}
        PaperProps={{ style: { width: 204 }, ...({ "data-kit-root": "" } as object) }}
      >
        <li><button type="button" className="hs-menu__item">Unfollow</button></li>
        <hr className="hs-menu__divider" />
        <li><button type="button" className="hs-menu__item">View all properties</button></li>
        <li><button type="button" className="hs-menu__item">View property history</button></li>
        <hr className="hs-menu__divider" />
        <li><button type="button" className="hs-menu__item">View association history</button></li>
      </HsMenu>
    ),
  },
  {
    id: "tooltip",
    label: "Tooltip",
    states: ["open"],
    floating: true,
    render: () => (
      <div style={{ paddingTop: 80, paddingLeft: 80 }}>
        <OuTooltip open title="Make a phone call" placement="top" TransitionProps={{ timeout: 0 }} slotProps={{ tooltip: { ...({ "data-kit-root": "" } as object) } }}>
          <button type="button" className="hs-btn hs-btn--secondary hs-btn--icon" style={{ visibility: "hidden" }}>
            <HsCallIcon />
          </button>
        </OuTooltip>
      </div>
    ),
  },
  {
    id: "popover",
    label: "Popover with arrow",
    states: ["open"],
    render: () => (
      <div style={{ paddingTop: 40 }}>
        <HsPopover arrow="top" arrowLeft={16} style={{ width: 617 }} data-kit-root="">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div style={{ width: 410 }}>
              <h2 style={{ margin: "0 0 16px", height: 72, font: "500 18px/24px var(--hs-sans)" }}>Verify every email and phone before your team reaches out</h2>
              <div style={{ height: 72, margin: "0 0 16px" }}>
                Get started with <a className="hs-link" href="#kit">boring for HubSpot</a> to check every contact as it lands in a sequence.
              </div>
              <OuPrimaryButton endIcon={<HsExternalIcon />}>Get started</OuPrimaryButton>
            </div>
            <div style={{ width: 133, height: 100, marginLeft: 24 }} />
          </div>
        </HsPopover>
      </div>
    ),
  },
  {
    id: "tabs",
    label: "Tabs (boxed)",
    states: ["default"],
    render: () => (
      <div style={{ width: 941 }}>
        <HsTabs
          fill
          data-kit-root=""
          value="b"
          onChange={() => undefined}
          options={[
            { value: "a", label: "Overview" },
            { value: "b", label: "Activities" },
            { value: "c", label: "Intelligence" },
          ]}
        />
      </div>
    ),
  },
  {
    id: "tabs-underline",
    label: "Tabs (underline)",
    states: ["default"],
    render: () => (
      <div style={{ width: 600 }}>
        <HsTabs
          variant="underline"
          data-kit-root=""
          value="a"
          onChange={() => undefined}
          options={[
            { value: "a", label: "People" },
            { value: "b", label: "Companies" },
          ]}
        />
      </div>
    ),
  },
  {
    id: "view-tab",
    label: "View tab (selected)",
    states: ["default"],
    render: () => (
      <div data-kit-root style={{ height: 40, display: "inline-block" }}>
        <HsViewTabs icon={<HsDragIcon />} value="a" onChange={() => undefined} options={[
            {
              value: "a",
              label: (
                <>
                  <span style={{ display: "inline-block", width: 53.5, overflow: "hidden" }}>All</span>
                  <span style={{ display: "inline-block", width: 16 }} />
                </>
              ),
            },
          ]} />
      </div>
    ),
  },
  {
    id: "table",
    label: "Index table",
    states: ["default", "hover"],
    render: () => (
      <div data-kit-root style={{ width: 1328, overflow: "hidden" }}>
        <TableSample />
      </div>
    ),
  },
  {
    id: "pagination",
    label: "Pagination",
    states: ["default"],
    render: () => (
      <div style={{ display: "inline-flex" }}>
        <HsPagination page={0} pages={11} onChange={() => undefined} data-kit-root="" />
      </div>
    ),
  },
  {
    id: "card",
    label: "Card",
    states: ["default"],
    render: () => (
      <div style={{ width: 640 }}>
        <HsCard
          data-kit-root=""
          title="Data highlights"
          toolbar={
            <OuIconButton xs aria-label="Card settings">
              <HsSettingsIcon />
            </OuIconButton>
          }
        >
          <HsHighlights
            items={[
              { label: "Create date", value: "10/04/2026 09:12 AM" },
              { label: "Lifecycle stage", value: "Lead" },
              { label: "Last activity date", value: "10/03/2026 4:20 PM" },
            ]}
          />
        </HsCard>
      </div>
    ),
  },
  {
    id: "skeleton",
    label: "Skeleton row",
    states: ["default"],
    render: () => (
      <div style={{ width: 480 }}>
        <HsSkeletonRow data-kit-root="" />
      </div>
    ),
  },
  {
    id: "dialog",
    label: "Modal dialog",
    states: ["open"],
    floating: true,
    render: () => (
      <HsDialog
        open
        hideBackdrop
        transitionDuration={0}
        width={1200}
        title="Merge duplicate records"
        onClose={() => undefined}
        paperProps={{ "data-kit-root": "" }}
        footer={
          <>
            <OuPrimaryButton disabled>Merge</OuPrimaryButton>
            <OuSecondaryButton>Cancel</OuSecondaryButton>
          </>
        }
      />
    ),
  },
  {
    id: "filter-dropdown",
    label: "Filter dropdown (open)",
    states: ["open"],
    render: () => (
      <div style={{ paddingTop: 40 }}>
        <HsPopover arrow="top" arrowLeft={42} padded={false} style={{ width: 350 }} data-kit-root="">
          <div style={{ margin: "12px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ height: 40 }}>
              <HsDropdownButton use="transparent" style={{ height: 40, padding: "10px 12px 10px 0" }}>
                is any of
              </HsDropdownButton>
            </div>
            <button type="button" className="hs-select hs-focusable" style={{ height: 35 }}>
              <span className="hs-select__label" />
              <span className="hs-caret hs-caret--lg" />
            </button>
          </div>
        </HsPopover>
      </div>
    ),
  },
  {
    id: "toggle",
    label: "Toggle (UNVERIFIED: no HubSpot capture)",
    states: ["default"],
    render: () => <ToggleSample />,
  },
  {
    id: "tags",
    label: "Tags and alert (UNVERIFIED: no HubSpot capture)",
    states: ["default"],
    render: () => (
      <div data-kit-root style={{ display: "flex", flexDirection: "column", gap: 12, width: 480 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <OuPill tone="success">Valid</OuPill>
          <OuPill tone="warning">Questionable</OuPill>
          <OuPill tone="danger">Invalid</OuPill>
          <OuPill tone="info">Enriched</OuPill>
          <OuPill tone="neutral">Unknown</OuPill>
        </div>
        <OuNotice tone="info">Every contact is checked when it lands in a sequence.</OuNotice>
        <div style={{ display: "flex", gap: 8 }}>
          <OuBone variant="text" width="40%" />
        </div>
      </div>
    ),
  },

  /* ── page-level parts (the restructured Insights, Users and Settings pages) ── */
  {
    id: "search-settings",
    label: "Search input, settings panel (40px)",
    states: ["default", "focus"],
    render: () => (
      <div data-kit-root style={{ width: 207 }}>
        <SearchSample variant="lg" width={207} />
      </div>
    ),
  },
  {
    id: "search-table",
    label: "Search input, list (30px)",
    states: ["default", "focus"],
    render: () => (
      <div data-kit-root style={{ width: 290 }}>
        <SearchSample variant="table" width={290} />
      </div>
    ),
  },
  {
    id: "settings-panel-header",
    label: "Settings panel header (back link + Search Settings)",
    states: ["default"],
    render: () => (
      <aside className="hs-settings__panel" style={{ position: "static", height: "auto", border: 0 }}>
        <header className="hs-settings__panel-header" data-kit-root>
          <button type="button" className="hs-settings__back">
            <HsLeftIcon />
            Dashboard
          </button>
          <SearchSample variant="lg" width="100%" placeholder="Search Settings" />
        </header>
      </aside>
    ),
  },
  {
    id: "settings-nav-item",
    label: "Settings panel item (selected)",
    states: ["default"],
    render: () => (
      <div style={{ width: 211, paddingLeft: 12 }}>
        <button type="button" className="hs-settings__item" aria-current="page" data-kit-root>
          Users &amp; Teams
        </button>
      </div>
    ),
  },
  {
    id: "boxed-tabs",
    label: "Boxed tabs (settings page)",
    states: ["default"],
    render: () => (
      <HubSpotBoxedTabs
        data-kit-root=""
        tab="general"
        tabs={[
          { value: "general", label: "General" },
          { value: "user", label: "User Defaults" },
          { value: "notify", label: "Notification Profiles" },
          { value: "currency", label: "Currency" },
          { value: "features", label: "Feature Releases" },
        ]}
      />
    ),
  },
  {
    id: "form-input",
    label: "Settings form text field (40px, 450 wide)",
    states: ["default"],
    render: () => (
      <div className="hs-settings-body">
        <div data-kit-root style={{ width: 450 }}>
          <Input defaultValue="boring-test" />
        </div>
      </div>
    ),
  },
  {
    id: "form-select",
    label: "Settings form select (40px, 450 wide)",
    states: ["default"],
    render: () => (
      <div className="hs-settings-body">
        <div data-kit-root style={{ width: 450 }}>
          <Select fullWidth value="jan" options={[{ value: "jan", label: "January - December" }]} onChange={() => undefined} />
        </div>
      </div>
    ),
  },
  {
    id: "count-tabs",
    label: "View tabs with counts",
    states: ["default"],
    render: () => (
      <div data-kit-root style={{ width: 956 }}>
        <HsCountTabs
          value="active"
          onChange={() => undefined}
          options={[
            { value: "active", label: "Active Users", count: 1 },
            { value: "pending", label: "Pending Invites", count: 0 },
            { value: "bounced", label: "Bounced Invites", count: 0 },
            { value: "deactivated", label: "Deactivated Users", count: 0 },
          ]}
        />
      </div>
    ),
  },
  {
    id: "badge-lavender",
    label: "Badge (Super Admin)",
    states: ["default"],
    render: () => (
      <span data-kit-root style={{ display: "inline-flex" }}>
        <HsBadge tone="lavender">aaaaa aaaaa</HsBadge>
      </span>
    ),
  },
  {
    id: "badge-grey",
    label: "Badge (report date range)",
    states: ["default"],
    render: () => (
      <span data-kit-root style={{ display: "inline-flex" }}>
        <HsBadge>This entire month</HsBadge>
      </span>
    ),
  },
  {
    id: "dashboard-title",
    label: "Dashboard title (star + dropdown)",
    states: ["default"],
    render: () => (
      <div data-kit-root style={{ display: "inline-flex" }}>
        <HsDashboardTitle title="CRM Data Overview" onOpen={() => undefined} />
      </div>
    ),
  },
  {
    id: "quick-filter",
    label: "Dashboard quick filter (Date range)",
    states: ["default"],
    render: () => (
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex" }}>
        <HsQuickFilter label="Date range" onOpen={() => undefined} data-kit-root="" />
      </ul>
    ),
  },
  {
    id: "advanced-filters",
    label: "Advanced filters button",
    states: ["default"],
    render: () => (
      <span data-kit-root style={{ display: "inline-flex" }}>
        <HsAdvancedFiltersButton onClick={() => undefined} />
      </span>
    ),
  },
  {
    id: "report-card",
    label: "Report card with KPI",
    states: ["default"],
    render: () => (
      <div data-kit-root style={{ width: 392, height: 191, display: "grid" }}>
        <HsReportCard title="New Contacts Created" info="New contacts" chips={["This entire month", "Compared To | Last month"]}>
          <HsDataWell items={[{ label: "(Count) Contacts", value: "2" }]} />
        </HsReportCard>
      </div>
    ),
  },
  {
    id: "datawell",
    label: "KPI data well",
    states: ["default"],
    render: () => (
      <div data-kit-root style={{ width: 342 }}>
        <HsDataWell items={[{ label: "(Count) Contacts", value: "2" }]} />
      </div>
    ),
  },
  {
    id: "daterange-popover",
    label: "Date range filter popover",
    states: ["open"],
    floating: true,
    render: () => (
      <div className="hs-popover" data-kit-root style={{ display: "inline-block" }}>
        <div className="hs-filter-pop">
          <HsFilterPopoverHead title="Date range" type="Datetime" typeIcon={<HsCalendarIcon />} applyDisabled onApply={() => undefined} onRemove={() => undefined} />
          <div className="hs-filter-pop__body">
            <p className="hs-filter-pop__hint">This is a dynamic quick filter that filters the dashboard based on date range.</p>
          </div>
        </div>
      </div>
    ),
  },
];

const CheckboxSample: FC = () => {
  const [a, setA] = useState(false);
  const [b, setB] = useState(true);
  return (
    <div style={{ display: "flex", gap: 16 }}>
      <span data-kit-root style={{ display: "inline-flex" }}>
        <OuCheckbox checked={a} onChange={setA} aria-label="Unchecked" />
      </span>
      <OuCheckbox checked={b} onChange={setB} aria-label="Checked" />
      <OuCheckbox checked={false} indeterminate onChange={() => undefined} aria-label="Some" />
    </div>
  );
};

const SearchSample: FC<{ variant?: "md" | "lg" | "table"; width?: number | string; placeholder?: string }> = ({ variant = "md", width = 160, placeholder = "" }) => {
  const [v, setV] = useState("");
  return <OuSearch value={v} onChange={setV} width={width} variant={variant} placeholder={placeholder} data-kit-target="" />;
};

const ToggleSample: FC = () => {
  const [on, setOn] = useState(true);
  const [off, setOff] = useState(false);
  return (
    <div data-kit-root style={{ display: "flex", gap: 16, alignItems: "center" }}>
      <OuSwitch checked={on} onChange={setOn} aria-label="On" />
      <OuSwitch checked={off} onChange={setOff} aria-label="Off" />
      <OuSwitch checked onChange={() => undefined} disabled aria-label="Disabled" />
      <OuSwitch checked onChange={() => undefined} saving aria-label="Saving" />
    </div>
  );
};

const DevHubSpotKit: FC = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const only = params.get("only");
  const theme = params.get("theme") === "dark" ? "dark" : "light";

  useEffect(() => {
    if (!only) return;
    const prev = document.body.style.background;
    document.body.style.background = theme === "dark" ? "#141414" : "#ffffff";
    return () => {
      document.body.style.background = prev;
    };
  }, [only, theme]);

  if (isProduction) return <Navigate to="/" replace />;

  if (only) {
    const sample = SAMPLES.find((x) => x.id === only);
    if (!sample) return <div>No sample “{only}”.</div>;
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: theme === "dark" ? "#141414" : "#ffffff", padding: 64, overflow: "auto" }}>
        {sample.render("default")}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32, padding: "24px 0", fontFamily: "var(--hs-sans)", color: "var(--hs-text)" }}>
      <div>
        <h1 style={{ font: "500 20px/24px var(--hs-sans)", margin: 0 }}>HubSpot kit</h1>
        <p style={{ margin: "4px 0 0", color: "var(--hs-text-secondary)" }}>
          Each part in each state. Diffed against the live HubSpot captures by app/scripts/hubspot-visual. Floating parts open one at a time with ?only=&lt;id&gt;.
        </p>
      </div>
      {SAMPLES.filter((x) => !x.floating).map((sample) => (
        <section key={sample.id} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <h2 style={{ font: "600 16px/20px var(--hs-sans)", margin: 0 }}>
            {sample.label} <a href={`?crmTheme=hubspot&only=${sample.id}`} style={{ fontWeight: 300, fontSize: 12 }}>only</a>
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
            {sample.states.map((s) => (
              <figure key={s} style={{ margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                <div>{sample.render(s)}</div>
                <figcaption style={{ font: "300 12px/14px var(--hs-sans)", color: "var(--hs-text-secondary)" }}>{s}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      ))}
      <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <h2 style={{ font: "600 16px/20px var(--hs-sans)", margin: 0 }}>Floating parts</h2>
        {SAMPLES.filter((x) => x.floating).map((sample) => (
          <a key={sample.id} href={`?crmTheme=hubspot&only=${sample.id}`}>
            {sample.label}
          </a>
        ))}
      </section>
    </div>
  );
};

export default DevHubSpotKit;
